import { useState, useCallback, useMemo, useRef } from 'react';
import * as yup from 'yup';

type FormValues = Record<string, any>;

type Error = {
  message: string;
  type?: string;
  params?: Record<string, any>;
  value?: any;
};

type Errors = Record<string, Error>;

interface IUseFormProps<T extends FormValues> {
  initialValues: T;
  validationSchema: yup.ObjectSchema<T>;
  ignoreValidations?: boolean;
  /**
   * Fields that must never mark the form dirty — for values that are synced into the
   * form programmatically (effects, sibling APIs) rather than edited by the user.
   * Pass a stable reference (module constant or memo).
   */
  nonDirtyKeys?: Array<keyof T>;
  onSubmit: (values: T) => Promise<void> | void;
}

interface IUseFormReturn<T extends FormValues> {
  values: T;
  errors: Errors;
  isSubmitted: boolean;
  setValue: <K extends keyof T>(key: K, value: T[K]) => void;
  setError: (key: string, error: Error | null) => void;
  setErrors: (errorsObj: Record<string, Error | null | undefined>) => void;
  handleSubmit: (e?: React.FormEvent) => Promise<void>;
  resetForm: (newValues?: Partial<T> | T) => void;
  resetField: <K extends keyof T>(fieldName: K, newValue?: T[K]) => void;
  /**
   * Reads the interaction based dirty state — true from the first time a field is actually
   * changed (a setValue whose value differs from the current one) until resetForm.
   * Erasing a field and re-typing the same text therefore counts as dirty: that is
   * intentional, since a false "clean" would silently drop data while a false "dirty"
   * only costs one redundant request. Backed by a ref, so it never re-renders and never
   * goes stale inside a submit handler's closure.
   */
  getIsDirty: () => boolean;
}

const useForm = <T extends FormValues>({
  initialValues,
  validationSchema,
  ignoreValidations,
  nonDirtyKeys,
  onSubmit,
}: IUseFormProps<T>): IUseFormReturn<T> => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // See getIsDirty in IUseFormReturn for the semantics.
  const isDirtyRef = useRef(false);
  const nonDirtyKeySet = useMemo(() => new Set((nonDirtyKeys || []).map(String)), [nonDirtyKeys]);

  const setValue = useCallback(
    <K extends keyof T>(key: K, value: T[K], errorKey?: string) => {
      setValues(prev => {
        if (prev[key] === value) return prev;
        if (!nonDirtyKeySet.has(String(key))) isDirtyRef.current = true;
        return { ...prev, [key]: value };
      });

      setErrors(prev => {
        const keyStr = String(errorKey || key);
        if (!(keyStr in prev)) return prev;
        const newErrors = { ...prev };
        delete newErrors[keyStr];
        return newErrors;
      });
    },
    [nonDirtyKeySet],
  );

  const setError = useCallback((key: string, error?: Error | null | undefined) => {
    setErrors(prev => {
      if (!error) {
        if (!(key in prev)) return prev;
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      }
      if (JSON.stringify(prev[key]) === JSON.stringify(error)) return prev;
      return { ...prev, [key]: error };
    });
  }, []);

  const setMultipleErrors = useCallback((errorsObj: Record<string, Error | null | undefined>) => {
    setErrors(prev => {
      let changed = false;
      let updated = { ...prev };
      if (Object.keys(errorsObj || {}).length === 0) return {};
      for (const key in errorsObj) {
        const value = errorsObj[key];
        if (!value) {
          if (key in updated) {
            delete updated[key];
            changed = true;
          }
        } else if (JSON.stringify(updated[key]) !== JSON.stringify(value)) {
          updated[key] = value;
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, []);

  const resetForm = useCallback(
    (newValues?: Partial<T> | T) => {
      setValues(newValues ? { ...initialValues, ...newValues } : initialValues);
      setErrors({});
      setIsSubmitted(false);
      isDirtyRef.current = false;
    },
    [initialValues],
  );

  const resetField = useCallback(
    <K extends keyof T>(fieldName: K, newValue?: T[K]) => {
      setValues(prev => {
        const targetValue = newValue !== undefined ? newValue : initialValues[fieldName];
        if (prev[fieldName] === targetValue) return prev;
        if (!nonDirtyKeySet.has(String(fieldName))) isDirtyRef.current = true;
        return { ...prev, [fieldName]: targetValue };
      });

      setErrors(prev => {
        const keyStr = String(fieldName);
        if (!(keyStr in prev)) return prev;
        const newErrors = { ...prev };
        delete newErrors[keyStr];
        return newErrors;
      });
    },
    [initialValues, nonDirtyKeySet],
  );

  const getIsDirty = useCallback(() => isDirtyRef.current, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      setIsSubmitted(true);
      try {
        if (!ignoreValidations) {
          await validationSchema.validate(values, { abortEarly: false });
          setErrors(curr => (Object.keys(curr).length > 0 ? {} : curr));
        }
        await onSubmit(values);
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          const fieldErrors: Errors = {};
          err.inner.forEach(error => {
            if (error.path) {
              fieldErrors[error.path] = {
                message: error.message,
                type: error.type,
                params: error.params,
                value: error.value,
              };
            }
          });
          setErrors(fieldErrors);
        } else {
          throw err;
        }
      }
    },
    [validationSchema, values, onSubmit],
  );

  return {
    values,
    errors,
    isSubmitted,
    setValue,
    setError,
    setErrors: setMultipleErrors,
    handleSubmit,
    resetForm,
    resetField,
    getIsDirty,
  };
};

export default useForm;
